import * as React from "react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router-dom";
import { Instance } from "mobx-state-tree";
import { mk8sDryRun } from "../../../../mobxStores/dryRun/mk8s";
import { mk8sMobx } from "../../../../mst/kinds/mk8s/mk8s";
import { Mk8sDraftMobx } from "../../../../mst/stores/mk8s.draft";
import { Mk8sDraftProviderPaperspaceMobx } from "../../../../mst/stores/mk8s.draft.provider.paperspace";
import { Mk8sDraftProviderPaperspaceNodePoolModel } from "../../../../mst/stores/mk8s.draft.provider.paperspace.nodepool";
import { homeLink, request } from "../../../../services/cpln";
import { DryRunAlert } from "../../dryRunAlert";
import { NGButton } from "../../../../newcomponents/button/Button";
import { NGFormElement } from "../../../../newcomponents/ngformelement/ngformelement";
import { MoreHorizontal } from "react-feather";
import { NGProviderBrowser } from "../../ngProviderBrowser";
import { NGSelect } from "../../../../newcomponents/select/ngselect";
import { NGInput } from "../../../../newcomponents/input/input";
import { NGInputListMst } from "../../../../newcomponents/inputList/inputListMst";
import { getDryRunErrorText } from "../../getDryRunErrorText";
import { DetectedTags, useHasDetectedTags } from "../../../../components/detail/detectedTags";

interface Props {
  mk8s: mk8sMobx;
  mk8sDraft: Mk8sDraftMobx;
  paperspace: Mk8sDraftProviderPaperspaceMobx;
  nodePool: Instance<typeof Mk8sDraftProviderPaperspaceNodePoolModel>;
  index: number;
}
const ProviderPaperspaceNodePoolRaw: React.FC<Props> = ({ mk8sDraft, paperspace: p, nodePool: np, index }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [browserKey, setBrowserKey] = React.useState("");

  const hasDetectedTags = useHasDetectedTags({
    value: np.labels.items,
    detectValue: (labels, key) => labels.some((l) => l.firstValue === key),
    detectionMap: {
      "cpln.io/nodeType": "if value is core: nodes can accept BYOK core components",
    },
  });

  const pre = `paperspace.nodePools[${index}].`;

  const labelsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.paperspace.nodePools[${index}].labels`,
    paths: np.labels.items.map((i) => `spec.provider.paperspace.nodePools[${index}].labels.${i.firstValue}`),
  });
  const taintsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.paperspace.nodePools[${index}].taints`,
    paths: [
      ...np.taints.items.map((_, idx) => `spec.provider.paperspace.nodePools[${index}].taints[${idx}]`),
      ...np.taints.items.map((_, idx) => `spec.provider.paperspace.nodePools[${index}].taints[${idx}].key`),
      ...np.taints.items.map((_, idx) => `spec.provider.paperspace.nodePools[${index}].taints[${idx}].value`),
      ...np.taints.items.map((_, idx) => `spec.provider.paperspace.nodePools[${index}].taints[${idx}].effect`),
    ],
  });

  return (
    <>
      <div>
        <NGFormElement
          name={`${pre}name`}
          label={np.name.label}
          value={np.name.value}
          onChange={np.name.setValue}
          required
          error={!p.isNodePoolNameValid(np.index) ? "Node pool names must be unique" : ""}
        />

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}machineType`}
            label={np.machineType.label}
            value={np.machineType.value}
            onChange={np.machineType.setValue}
            required={np.machineType.isRequired}
            error={np.machineType.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => setBrowserKey("machine-types"),
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.machineType.setValue}
              path={`spec.provider.paperspace.nodePools[${index}].machineType`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}publicIpType`}
            label={np.publicIpType.label}
            value={np.publicIpType.value}
            onChange={np.publicIpType.setValue}
            options={np.publicIpType.options}
            required={true}
            error={np.publicIpType.value ? "" : "Public Ip Type is required."}
            as={"select"}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.publicIpType.setValue}
              path={`spec.provider.paperspace.nodePools[${index}].publicIpType`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}bootDiskSize`}
            label={np.bootDiskSize.label}
            value={np.bootDiskSize.value}
            onChange={np.bootDiskSize.setValue}
            options={np.bootDiskSize.options}
            as={"select"}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.bootDiskSize.setValue}
              path={`spec.provider.paperspace.nodePools[${index}].bootDiskSize`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}minSize`}
            label={np.minSize.label}
            value={np.minSize.value}
            onChange={np.minSize.setValue}
            required={np.minSize.isRequired}
            error={np.minSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.minSize.setValue}
              path={`spec.provider.paperspace.nodePools[${index}].minSize`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}maxSize`}
            label={np.maxSize.label}
            value={np.maxSize.value}
            onChange={np.maxSize.setValue}
            required={np.maxSize.isRequired}
            error={np.maxSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.maxSize.setValue}
              path={`spec.provider.paperspace.nodePools[${index}].maxSize`}
            />
          </div>
        </div>
        <NGInputListMst
          data={np.labels}
          className={hasDetectedTags ? "mb-2" : "mb-8"}
          label="Labels"
          labelRender={() => {
            return (
              <>
                {np.labels.items.some((l) => l.firstValue === "cpln.io/nodeType" && l.secondValue === "core") ? null : (
                  <NGButton
                    variant="primary"
                    className="ml-2"
                    onClick={() => {
                      const id = np.labels.add();
                      const item = np.labels.items.find((i) => i.id === id);
                      if (item) {
                        item.setFirstValue("cpln.io/nodeType");
                        item.setSecondValue("core");
                      }
                    }}
                    size={`small`}
                  >
                    Use this nodepool as BYOK core
                  </NGButton>
                )}
              </>
            );
          }}
          firstLabel="Key"
          firstInput={(i) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && labelsDryRun.message.includes(`.${i.firstValue}`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          invalid={labelsDryRun.severity === "error"}
          error={labelsDryRun.severity === "error" ? labelsDryRun.message : ""}
          warning={labelsDryRun.severity === "error" ? "" : labelsDryRun.message}
        />
        <DetectedTags
          className="mb-8"
          value={np.labels.items}
          detectValue={(labels, key) => labels.some((l) => l.firstValue === key)}
          detectionMap={{
            "cpln.io/nodeType": "if value is core: nodes can accept BYOK core components",
          }}
          pluralLabel="Labels"
        />
        <NGInputListMst
          data={np.taints}
          className="mb-8"
          label="Taints"
          firstLabel="Key"
          firstInput={(i, index) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && taintsDryRun.message.includes(`[${index}].key`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }
            if (i.secondValue && taintsDryRun.message.includes(`[${index}].value`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          thirdLabel="Effect"
          thirdInput={(i) => {
            let invalid = false;
            if (i.thirdValue && taintsDryRun.message.includes(`[${index}].effect`)) {
              invalid = true;
            }

            return (
              <NGSelect
                invalid={invalid}
                value={i.thirdValue}
                onChange={(value) => i.setThirdValue(value)}
                options={[
                  { label: "NoSchedule", value: "NoSchedule" },
                  { label: "PreferNoSchedule", value: "PreferNoSchedule" },
                  { label: "NoExecute", value: "NoExecute" },
                ]}
              />
            );
          }}
          invalid={taintsDryRun.severity === "error"}
          error={taintsDryRun.severity === "error" ? taintsDryRun.message : ""}
          warning={taintsDryRun.severity === "error" ? "" : taintsDryRun.message}
        />
        <NGButton
          style={{ width: 220 }}
          variant={"danger"}
          outlined
          onClick={() => {
            p.removeNodePoolAt(np.index);
            navigate(`${pathname.split("/-node-pools")[0]}/-node-pools`);
          }}
        >
          Delete Node Pool
        </NGButton>
      </div>
      {browserKey === "machine-types" ? (
        <NGProviderBrowser
          title="Browse Paperspace Machine Types"
          initialValue={np.machineType.value}
          fetchData={async () => {
            // TODOMK8S paginate
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "machine-types",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.data;
          }}
          sortable
          onOk={(value) => np.machineType.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Type", "Cores", "Memory", "GPU", "GPU Count", "Supports Nv Link", "Cost per Month"]}
          rightAlign={["Cost per Month"]}
          distinctFilters={["Cores", "Memory", "GPU", "GPU Count", "Supports Nv Link"]}
          getData={(item: any) => {
            const pricePerDay = Number(item.pricePerHour || 0) * 24;
            const pricePerMonth = pricePerDay * 30;
            const cost = pricePerMonth.toLocaleString("en-US", { style: "currency", currency: "USD" });

            return [
              //
              item.machineType,
              item.machineType,
              item.cores,
              [item.ram, `${item.ram}Gi`],
              item.gpu,
              item.gpuCount,
              [item.supportsNvlink, item.supportsNvlink ? "Yes" : "No"],
              [pricePerMonth, cost],
            ];
          }}
        />
      ) : null}
    </>
  );
};

export const ProviderPaperspaceNodePool = observer(ProviderPaperspaceNodePoolRaw);
